import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, ChevronDown, ChevronUp, BookOpen, FileText, Languages, Flag, Minus, Copy, Eye } from 'lucide-react';
import apiService from '../../services/api';
import { DocumentReader } from '../../word/api/DocumentReader';
import { ProofreadWordAdapter } from '../../features/proofread/ProofreadWordAdapter';
import { log, logError } from '../../utils/logging';
import { directionForLanguage } from '../../i18n/config';
import type {
  DefinitionIssue,
  DraftingNoteIssue,
  LanguageIssue,
  LanguageScanStatus,
  ProofreadStatus,
  WordCommentItem,
} from '../../features/proofread/types';
import './ProofreadView.css';

type FilterStatus = 'all' | 'open';

let _issueIdCounter = 0;
function nextId(): string {
  return `pf-${++_issueIdCounter}-${Date.now()}`;
}

function countOpen<T extends { status: ProofreadStatus }>(items: T[]): number {
  return items.filter((i) => i.status === 'open').length;
}

export const ProofreadView: React.FC = () => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  // --- state ---
  const [definitions, setDefinitions] = React.useState<DefinitionIssue[]>([]);
  const [draftingNotes, setDraftingNotes] = React.useState<DraftingNoteIssue[]>([]);
  const [wordComments, setWordComments] = React.useState<WordCommentItem[]>([]);
  const [languageIssues, setLanguageIssues] = React.useState<LanguageIssue[]>([]);
  const [scanStatus, setScanStatus] = React.useState<LanguageScanStatus>('idle');
  const [scanError, setScanError] = React.useState<string | null>(null);
  const [filterStatus, setFilterStatus] = React.useState<FilterStatus>('open');
  const [docLoadError, setDocLoadError] = React.useState<string | null>(null);
  const [replaceAllResult, setReplaceAllResult] = React.useState<{ succeeded: number; failed: number } | null>(null);
  const [isDocumentScanning, setIsDocumentScanning] = React.useState(false);

  // expanded category state
  const [defExpanded, setDefExpanded] = React.useState(true);
  const [defMultipleExpanded, setDefMultipleExpanded] = React.useState(false);
  const [defUnusedExpanded, setDefUnusedExpanded] = React.useState(false);
  const [draftExpanded, setDraftExpanded] = React.useState(true);
  const [draftPlaceholderExpanded, setDraftPlaceholderExpanded] = React.useState(false);
  const [draftCommentExpanded, setDraftCommentExpanded] = React.useState(false);
  const [wordCommentsExpanded, setWordCommentsExpanded] = React.useState(true);
  const [langExpanded, setLangExpanded] = React.useState(true);

  // Whether the user has triggered at least one scan (for neutral pre-scan empty states)
  const [hasScanned, setHasScanned] = React.useState(false);

  async function runDocumentScan() {
    if (isDocumentScanning) {
      log('[ProofreadFlow] runDocumentScan:skipped_already_scanning');
      return;
    }
    const startedAt = Date.now();
    setIsDocumentScanning(true);
    setScanStatus('scanning');
    setReplaceAllResult(null);
    setDocLoadError(null);
    setScanError(null);
    log('[ProofreadFlow] runDocumentScan:start');
    try {
      const { paragraphs } = await DocumentReader.getFullDocumentContext();
      const fullText = paragraphs.map((p) => p.text).join('\n');
      log('[ProofreadFlow] runDocumentScan:context_ready', {
        paragraphCount: paragraphs.length,
        fullTextLength: fullText.length,
      });

      const apiStart = Date.now();
      const raw = await apiService.proofreadDocument(fullText, paragraphs);
      log('[ProofreadFlow] runDocumentScan:api_done', {
        apiMs: Date.now() - apiStart,
        definitionsCount: raw.definitions.length,
        draftingNotesCount: raw.draftingNotes.length,
        languageCount: raw.language.length,
      });

      // Map definitions
      const newDefinitions: DefinitionIssue[] = raw.definitions.map((d) => ({
        id: nextId(),
        term: d.term,
        type: d.type as 'multiple' | 'unused',
        occurrences: d.occurrences,
        status: 'open' as ProofreadStatus,
      }));
      setDefinitions(newDefinitions);

      // Map drafting notes (AI-detected placeholders + internal notes)
      const newDraftingNotes: DraftingNoteIssue[] = raw.draftingNotes.map((n) => ({
        id: nextId(),
        type: n.type as 'placeholder' | 'internal_note',
        text: n.text,
        paragraphIndex: n.paragraphIndex,
        status: 'open' as ProofreadStatus,
      }));
      setDraftingNotes(newDraftingNotes);

      // Fetch actual Word comments via Office.js (independent of AI)
      const rawWordComments = await ProofreadWordAdapter.getWordComments();
      const newWordComments: WordCommentItem[] = rawWordComments.map((c) => ({
        id: c.id,
        author: c.author,
        text: c.text,
        anchorText: c.anchorText,
        status: 'open' as ProofreadStatus,
      }));
      setWordComments(newWordComments);
      log('[ProofreadFlow] runDocumentScan:word_comments', { count: newWordComments.length });

      // Map language issues
      const newLanguageIssues: LanguageIssue[] = raw.language.map((l) => ({
        id: nextId(),
        originalText: l.originalText,
        suggestion: l.suggestion,
        explanation: l.explanation || '',
        paragraphIndex: l.paragraphIndex,
        paragraphText: paragraphs[l.paragraphIndex]?.text || '',
        status: 'open' as ProofreadStatus,
      }));
      setLanguageIssues(newLanguageIssues);
      setScanStatus('done');
      setHasScanned(true);

      log('[ProofreadFlow] runDocumentScan:done', {
        definitions: newDefinitions.length,
        draftingNotes: newDraftingNotes.length,
        language: newLanguageIssues.length,
        totalMs: Date.now() - startedAt,
      });
    } catch (err: any) {
      logError('[ProofreadFlow] runDocumentScan:error', {
        message: err?.message || String(err),
        totalMs: Date.now() - startedAt,
      });
      setScanError(t('proofread.scanFailed'));
      setScanStatus('error');
    } finally {
      setIsDocumentScanning(false);
    }
  }

  // ---- Actions ----
  function ignoreDefinition(id: string) {
    setDefinitions((prev) => prev.map((d) => (d.id === id ? { ...d, status: 'ignored' } : d)));
  }
  function ignoreDraftingNote(id: string) {
    setDraftingNotes((prev) => prev.map((d) => (d.id === id ? { ...d, status: 'ignored' } : d)));
  }
  function ignoreWordComment(id: string) {
    setWordComments((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'ignored' } : c)));
  }
  function ignoreLanguageIssue(id: string) {
    setLanguageIssues((prev) => prev.map((l) => (l.id === id ? { ...l, status: 'ignored' } : l)));
  }

  async function replaceLanguageIssue(id: string) {
    const issue = languageIssues.find((l) => l.id === id);
    if (!issue) return;
    const ok = await ProofreadWordAdapter.replaceText(
      issue.originalText,
      issue.suggestion,
      issue.paragraphIndex
    );
    if (ok) {
      setLanguageIssues((prev) => prev.map((l) => (l.id === id ? { ...l, status: 'resolved' } : l)));
    }
  }

  async function replaceAllLanguage() {
    const open = languageIssues.filter((l) => l.status === 'open');
    const result = await ProofreadWordAdapter.replaceAll(
      open.map((l) => ({
        originalText: l.originalText,
        suggestion: l.suggestion,
        paragraphIndex: l.paragraphIndex,
      }))
    );
    setReplaceAllResult(result);
    if (result.succeeded > 0) {
      setLanguageIssues((prev) =>
        prev.map((l) =>
          l.status === 'open' ? { ...l, status: 'resolved' } : l
        )
      );
    }
  }

  function copyToClipboard(text: string) {
    try {
      navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  // ---- Filtering ----
  function visibleDefinitions(): DefinitionIssue[] {
    return filterStatus === 'open' ? definitions.filter((d) => d.status === 'open') : definitions;
  }
  function visibleDraftingNotes(): DraftingNoteIssue[] {
    return filterStatus === 'open' ? draftingNotes.filter((d) => d.status === 'open') : draftingNotes;
  }
  function visibleLanguage(): LanguageIssue[] {
    return filterStatus === 'open' ? languageIssues.filter((l) => l.status === 'open') : languageIssues;
  }

  const multipleDefinitions = visibleDefinitions().filter((d) => d.type === 'multiple');
  const unusedDefinitions = visibleDefinitions().filter((d) => d.type === 'unused');
  const placeholders = visibleDraftingNotes().filter((d) => d.type === 'placeholder');
  const internalNotes = visibleDraftingNotes().filter((d) => d.type === 'internal_note');
  const visibleWordComments = filterStatus === 'open'
    ? wordComments.filter((c) => c.status === 'open')
    : wordComments;

  const defOpenCount = countOpen(definitions);
  const draftOpenCount = countOpen(draftingNotes) + wordComments.filter((c) => c.status === 'open').length;
  const langOpenCount = countOpen(languageIssues);
  const totalOpen = defOpenCount + draftOpenCount + langOpenCount;

  // ---- Render ----
  return (
    <div className="proofread-view" dir={chromeDir}>
      {/* Header */}
      <div className="pf-header">
        <div className="pf-header-left">
          {totalOpen > 0 && (
            <span className="pf-total-badge">{t('proofread.issues', { count: totalOpen })}</span>
          )}
          <button
            className={`pf-filter-btn ${filterStatus === 'open' ? 'active' : ''}`}
            onClick={() => setFilterStatus(filterStatus === 'open' ? 'all' : 'open')}
          >
            {filterStatus === 'open' ? t('proofread.opened') : t('proofread.all')} <ChevronDown size={12} />
          </button>
        </div>
        <div className="pf-header-right">
          <button
            className="pf-top-scan-btn"
            onClick={runDocumentScan}
            disabled={isDocumentScanning}
            title={t('proofread.documentScanTitle')}
          >
            {isDocumentScanning ? (
              <>
                <RefreshCw size={13} className="spinning" />
                {t('proofread.scanning')}
              </>
            ) : (
              t('proofread.documentScan')
            )}
          </button>
        </div>
      </div>

      {docLoadError && <div className="pf-error">{docLoadError}</div>}

      <div className="pf-categories">
        {/* ---- DEFINITIONS ---- */}
        <div className="pf-category">
          <button className="pf-cat-header" onClick={() => setDefExpanded((v) => !v)}>
            <span className="pf-cat-icon"><BookOpen size={16} /></span>
            <span className="pf-cat-title">{t('proofread.definitions')}</span>
            {defOpenCount > 0 && <span className="pf-cat-count">{defOpenCount}</span>}
            <span className="pf-cat-caret">{defExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
          </button>

          {defExpanded && (
            <div className="pf-cat-body">
              {scanStatus === 'scanning' ? (
                <div className="pf-lang-scanning">
                  <RefreshCw size={16} className="spinning" />
                  <span>{t('proofread.scanningDocument')}</span>
                </div>
              ) : (
                <>
              {/* Multiple Definitions */}
              {multipleDefinitions.length > 0 && (
                <div className="pf-subcat">
                  <button
                    className="pf-subcat-header"
                    onClick={() => setDefMultipleExpanded((v) => !v)}
                  >
                    <span className="pf-subcat-dot pf-dot-purple" />
                    <span className="pf-subcat-title">{t('proofread.multipleDefinitions')}</span>
                    <span className="pf-subcat-count">{multipleDefinitions.length}</span>
                    <span className="pf-subcat-caret">
                      {defMultipleExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  </button>
                  {defMultipleExpanded &&
                    multipleDefinitions.map((def) => (
                      <div key={def.id} className="pf-def-item">
                        <div className="pf-def-item-row">
                          <span className="pf-def-term" dir="auto">{def.term}</span>
                          <div className="pf-def-actions">
                            <button
                              className="pf-icon-btn pf-ignore-btn"
                              title={t('proofread.ignore')}
                              onClick={() => ignoreDefinition(def.id)}
                            >
                              <Minus size={12} />
                            </button>
                            <button className="pf-icon-btn" title={t('proofread.flag')}>
                              <Flag size={12} />
                            </button>
                          </div>
                        </div>
                        <div className="pf-def-snippets">
                          {def.occurrences.map((occ, i) => (
                            <button
                              key={i}
                              className="pf-snippet-chip"
                              dir="auto"
                              title={t('proofread.navigateOccurrence')}
                              onClick={() => ProofreadWordAdapter.navigateToParagraph(occ.paragraphIndex)}
                            >
                              {occ.snippet.length > 40 ? occ.snippet.substring(0, 40) + '…' : occ.snippet}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Unused Definitions */}
              {unusedDefinitions.length > 0 && (
                <div className="pf-subcat">
                  <button
                    className="pf-subcat-header"
                    onClick={() => setDefUnusedExpanded((v) => !v)}
                  >
                    <span className="pf-subcat-dot pf-dot-purple" />
                    <span className="pf-subcat-title">{t('proofread.unusedDefinitions')}</span>
                    <span className="pf-subcat-count">{unusedDefinitions.length}</span>
                    <span className="pf-subcat-caret">
                      {defUnusedExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  </button>
                  {defUnusedExpanded &&
                    unusedDefinitions.map((def) => (
                      <div key={def.id} className="pf-def-item">
                        <div className="pf-def-item-row">
                          <span className="pf-def-term" dir="auto">{def.term}</span>
                          <div className="pf-def-actions">
                            <button
                              className="pf-icon-btn pf-ignore-btn"
                              title={t('proofread.ignore')}
                              onClick={() => ignoreDefinition(def.id)}
                            >
                              <Minus size={12} />
                            </button>
                            <button
                              className="pf-icon-btn"
                              title={t('proofread.navigate')}
                              onClick={() => ProofreadWordAdapter.navigateToParagraph(def.occurrences[0]?.paragraphIndex ?? 0)}
                            >
                              <Eye size={12} />
                            </button>
                          </div>
                        </div>
                        <div className="pf-def-snippets">
                          {def.occurrences.map((occ, i) => (
                            <button
                              key={i}
                              className="pf-snippet-chip"
                              dir="auto"
                              onClick={() => ProofreadWordAdapter.navigateToParagraph(occ.paragraphIndex)}
                            >
                              {occ.snippet.length > 40 ? occ.snippet.substring(0, 40) + '…' : occ.snippet}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {multipleDefinitions.length === 0 && unusedDefinitions.length === 0 && (
                <div className="pf-empty-cat">
                  {hasScanned ? t('proofread.noDefinitionIssues') : t('proofread.runScanDefinitions')}
                </div>
              )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ---- DRAFTING NOTES ---- */}
        <div className="pf-category">
          <button className="pf-cat-header" onClick={() => setDraftExpanded((v) => !v)}>
            <span className="pf-cat-icon"><FileText size={16} /></span>
            <span className="pf-cat-title">{t('proofread.draftingNotes')}</span>
            {draftOpenCount > 0 && <span className="pf-cat-count">{draftOpenCount}</span>}
            <span className="pf-cat-caret">{draftExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
          </button>

          {draftExpanded && (
            <div className="pf-cat-body">
              {scanStatus === 'scanning' ? (
                <div className="pf-lang-scanning">
                  <RefreshCw size={16} className="spinning" />
                  <span>{t('proofread.scanningDocument')}</span>
                </div>
              ) : (
                <>
              {/* Placeholders */}
              {placeholders.length > 0 && (
                <div className="pf-subcat">
                  <button
                    className="pf-subcat-header"
                    onClick={() => setDraftPlaceholderExpanded((v) => !v)}
                  >
                    <span className="pf-subcat-dot pf-dot-purple" />
                    <span className="pf-subcat-title">{t('proofread.placeholders')}</span>
                    <span className="pf-subcat-count">{placeholders.length}</span>
                    <span className="pf-subcat-caret">
                      {draftPlaceholderExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  </button>
                  {draftPlaceholderExpanded &&
                    placeholders.map((note) => (
                      <div key={note.id} className="pf-draft-item">
                        <span className="pf-draft-text" dir="auto">{note.text}</span>
                        <div className="pf-def-actions">
                          <button
                            className="pf-icon-btn pf-ignore-btn"
                            title={t('proofread.ignore')}
                            onClick={() => ignoreDraftingNote(note.id)}
                          >
                            <Minus size={12} />
                          </button>
                          <button
                            className="pf-icon-btn"
                            title={t('proofread.navigate')}
                            onClick={() => ProofreadWordAdapter.navigateToParagraph(note.paragraphIndex)}
                          >
                            <Eye size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Internal Notes (AI-detected) */}
              {internalNotes.length > 0 && (
                <div className="pf-subcat">
                  <button
                    className="pf-subcat-header"
                    onClick={() => setDraftCommentExpanded((v) => !v)}
                  >
                    <span className="pf-subcat-dot pf-dot-purple" />
                    <span className="pf-subcat-title">{t('proofread.internalNotes')}</span>
                    <span className="pf-subcat-count">{internalNotes.length}</span>
                    <span className="pf-subcat-caret">
                      {draftCommentExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  </button>
                  {draftCommentExpanded &&
                    internalNotes.map((note) => (
                      <div key={note.id} className="pf-draft-item">
                        <span className="pf-draft-text" dir="auto">{note.text}</span>
                        <div className="pf-def-actions">
                          <button
                            className="pf-icon-btn pf-ignore-btn"
                            title={t('proofread.ignore')}
                            onClick={() => ignoreDraftingNote(note.id)}
                          >
                            <Minus size={12} />
                          </button>
                          <button
                            className="pf-icon-btn"
                            title={t('proofread.navigate')}
                            onClick={() => ProofreadWordAdapter.navigateToParagraph(note.paragraphIndex)}
                          >
                            <Eye size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Active Comments (Office.js Word comments) */}
              {visibleWordComments.length > 0 && (
                <div className="pf-subcat">
                  <button
                    className="pf-subcat-header"
                    onClick={() => setWordCommentsExpanded((v) => !v)}
                  >
                    <span className="pf-subcat-dot pf-dot-blue" />
                    <span className="pf-subcat-title">{t('proofread.activeComments')}</span>
                    <span className="pf-subcat-count">{visibleWordComments.length}</span>
                    <span className="pf-subcat-caret">
                      {wordCommentsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  </button>
                  {wordCommentsExpanded &&
                    visibleWordComments.map((wc) => (
                      <div key={wc.id} className="pf-draft-item">
                        <div className="pf-wc-body">
                          {wc.author && <span className="pf-wc-author" dir="auto">{wc.author}</span>}
                          <span className="pf-draft-text" dir="auto">{wc.text}</span>
                          {wc.anchorText && (
                            <span className="pf-wc-anchor" dir="auto">"{wc.anchorText}"</span>
                          )}
                        </div>
                        <div className="pf-def-actions">
                          <button
                            className="pf-icon-btn pf-ignore-btn"
                            title={t('proofread.dismiss')}
                            onClick={() => ignoreWordComment(wc.id)}
                          >
                            <Minus size={12} />
                          </button>
                          {wc.anchorText && (
                            <button
                              className="pf-icon-btn"
                              title={t('proofread.navigateAnchor')}
                              onClick={() => ProofreadWordAdapter.navigateByContent(wc.anchorText)}
                            >
                              <Eye size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {placeholders.length === 0 && internalNotes.length === 0 && visibleWordComments.length === 0 && (
                <div className="pf-empty-cat">
                  {hasScanned ? t('proofread.noDraftingNotes') : t('proofread.runScanDrafting')}
                </div>
              )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ---- LANGUAGE ---- */}
        <div className="pf-category">
          <button className="pf-cat-header" onClick={() => setLangExpanded((v) => !v)}>
            <span className="pf-cat-icon"><Languages size={16} /></span>
            <span className="pf-cat-title">{t('proofread.language')}</span>
            {langOpenCount > 0 && <span className="pf-cat-count">{langOpenCount}</span>}
            <span className="pf-cat-caret">{langExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
          </button>

          {langExpanded && (
            <div className="pf-cat-body">
              {scanStatus === 'idle' && (
                <div className="pf-empty-cat">{t('proofread.runScanLanguage')}</div>
              )}

              {scanStatus === 'scanning' && (
                <div className="pf-lang-scanning">
                  <RefreshCw size={16} className="spinning" />
                  <span>{t('proofread.scanningDocument')}</span>
                </div>
              )}

              {scanStatus === 'error' && (
                <div className="pf-error">
                  {scanError}
                  <button className="pf-retry-btn" onClick={runDocumentScan}>
                    {t('actions.retry', { ns: 'common' })}
                  </button>
                </div>
              )}

              {scanStatus === 'done' && (
                <>
                  {visibleLanguage().length === 0 ? (
                    <div className="pf-empty-cat">{t('proofread.noLanguageIssues')}</div>
                  ) : (
                    <div className="pf-lang-list">
                      {visibleLanguage().map((issue) => (
                        <div
                          key={issue.id}
                          className={`pf-lang-item${issue.status !== 'open' ? ' pf-lang-resolved' : ''}`}
                        >
                          <div className="pf-lang-item-text" dir="auto">
                            <span className="pf-lang-original">{issue.originalText}</span>
                            {issue.explanation ? (
                              <span className="pf-lang-explanation"> — {issue.explanation}</span>
                            ) : null}
                          </div>
                          <div className="pf-lang-redline" dir="auto">
                            <del className="pf-lang-del">{issue.originalText}</del>
                            <span className="pf-lang-arrow">→</span>
                            <ins className="pf-lang-ins">{issue.suggestion}</ins>
                          </div>
                          <div className="pf-lang-actions">
                            {issue.status === 'open' && (
                              <button
                                className="pf-replace-btn"
                                title={t('proofread.replaceWith', { suggestion: issue.suggestion })}
                                onClick={() => replaceLanguageIssue(issue.id)}
                              >
                                {t('proofread.replace')}
                              </button>
                            )}
                            <button
                              className="pf-icon-btn"
                              title={t('proofread.navigateParagraph')}
                              onClick={() =>
                                void ProofreadWordAdapter.focusClauseBeforeApply(
                                  issue.paragraphIndex,
                                  issue.originalText
                                )
                              }
                            >
                              <Eye size={12} />
                            </button>
                            {issue.status === 'open' && (
                              <>
                                <button
                                  className="pf-icon-btn"
                                  title={t('proofread.copySuggestion')}
                                  onClick={() => copyToClipboard(issue.suggestion)}
                                >
                                  <Copy size={12} />
                                </button>
                                <button
                                  className="pf-icon-btn pf-ignore-btn"
                                  title={t('proofread.ignore')}
                                  onClick={() => ignoreLanguageIssue(issue.id)}
                                >
                                  <Minus size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {visibleLanguage().filter((l) => l.status === 'open').length > 1 && (
                    <div className="pf-lang-footer">
                      <button className="pf-replace-all-btn" onClick={replaceAllLanguage}>
                        {t('proofread.replaceAll')}
                      </button>
                      <button
                        className="pf-icon-btn"
                        title={t('proofread.copyAllSuggestions')}
                        onClick={() =>
                          copyToClipboard(
                            visibleLanguage()
                              .filter((l) => l.status === 'open')
                              .map((l) => `"${l.originalText}" → "${l.suggestion}"`)
                              .join('\n')
                          )
                        }
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  )}

                  {replaceAllResult && (
                    <div className="pf-replace-summary">
                      {t('proofread.replacedCount', { count: replaceAllResult.succeeded })}
                      {replaceAllResult.failed > 0 &&
                        `, ${t('proofread.failedCount', { count: replaceAllResult.failed })}`}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
